import inChatSound from '@/assets/sounds/in-chat.ogg';
import messageSound from '@/assets/sounds/message.ogg';
import notificationSound from '@/assets/sounds/notification.ogg';
import sendSound from '@/assets/sounds/send.ogg';

export type SoundType = 'notification' | 'message' | 'inChat' | 'send';

const SOUND_FILES: Record<SoundType, string> = {
  notification: notificationSound,
  message: messageSound,
  inChat: inChatSound,
  send: sendSound,
};

const audioCache = new Map<SoundType, HTMLAudioElement>();
const loadingPromises = new Map<SoundType, Promise<HTMLAudioElement>>();

async function loadSound(type: SoundType): Promise<HTMLAudioElement> {
  const cached = audioCache.get(type);
  if (cached) return cached;

  const existing = loadingPromises.get(type);
  if (existing) return existing;

  const loadPromise = new Promise<HTMLAudioElement>((resolve, reject) => {
    const audio = new Audio(SOUND_FILES[type]);
    audio.preload = 'auto';

    const cleanup = () => {
      audio.removeEventListener('canplaythrough', onLoad);
      audio.removeEventListener('error', onError);
    };

    const onLoad = () => {
      cleanup();
      audioCache.set(type, audio);
      loadingPromises.delete(type);
      resolve(audio);
    };

    const onError = () => {
      cleanup();
      loadingPromises.delete(type);
      reject(new Error(`Failed to load sound: ${type}`));
    };

    audio.addEventListener('canplaythrough', onLoad, { once: true });
    audio.addEventListener('error', onError, { once: true });
    audio.load();
  });

  loadingPromises.set(type, loadPromise);
  return loadPromise;
}

async function loadSoundWithRetry(
  type: SoundType,
): Promise<HTMLAudioElement | null> {
  try {
    return await loadSound(type);
  } catch {
    // Retry once
    try {
      audioCache.delete(type);
      return await loadSound(type);
    } catch {
      // Silently fail after retry - sound is non-critical
      return null;
    }
  }
}

export async function playSound(type: SoundType): Promise<void> {
  try {
    const audio = await loadSoundWithRetry(type);
    if (!audio) return;

    // Clone for overlapping playback
    const clone = audio.cloneNode() as HTMLAudioElement;
    clone.play().catch(() => {
      // Silently fail if browser blocks autoplay
    });
  } catch {
    // Silently fail
  }
}

export function preloadSound(type: SoundType): void {
  loadSoundWithRetry(type).catch(() => {
    // Silently fail preload errors
  });
}

export function preloadAllSounds(): void {
  const types: SoundType[] = ['notification', 'message', 'inChat', 'send'];
  types.forEach(preloadSound);
}
